import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Share2, Download, Copy, Check, QrCode } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareTourQRCard — Apple-Wallet-pass styled card that gives the visitor an
 * instant way to share this tour with a friend nearby. A scan opens the same
 * tour page on the friend's phone. Pairs nicely with the proximity-sort and
 * "Get directions" features: turns a spontaneous "we should do this together"
 * moment into a one-scan handoff.
 *
 * Intentionally NO referral / Strides hook — keeps the card deploy-safe while
 * Phase 1 + 2 of the rewards ecosystem remain on hold for production.
 */
export const ShareTourQRCard = ({ tour }) => {
  const qrWrapperRef = useRef(null);
  const [copied, setCopied] = useState(false);

  if (!tour) return null;

  // Always use the canonical public origin so the QR is portable across
  // preview / production environments — and works from any device.
  const tourUrl = `${window.location.origin}/tours/${tour.tour_id}`;

  const handleShare = async () => {
    const shareData = {
      title: `${tour.title} · Audiostride`,
      text: `Check out this GPS-guided audio tour in ${tour.city || 'town'}:`,
      url: tourUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (_) { /* user cancelled — silent */ }
    // Fallback: copy link to clipboard
    try {
      await navigator.clipboard.writeText(tourUrl);
      setCopied(true);
      toast.success('Tour link copied — paste it anywhere');
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error('Could not copy the link');
    }
  };

  const handleDownload = () => {
    const canvas = qrWrapperRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('QR code not ready yet — try again in a second');
      return;
    }
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      const safeTitle = (tour.title || 'audiostride-tour')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      a.download = `${safeTitle}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('QR saved to your downloads');
    } catch (_) {
      toast.error('Could not download the QR');
    }
  };

  return (
    <div
      data-testid="share-tour-qr-card"
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl"
    >
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 bg-cyan-500/20 blur-3xl rounded-full" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 bg-emerald-400/10 blur-3xl rounded-full" />

      <div className="relative grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 p-6 sm:p-7">
        {/* Left — copy */}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] bg-white/10 border border-white/15 rounded-full px-3 py-1 mb-4">
            <QrCode className="w-3.5 h-3.5" />
            Walk this with a friend
          </div>
          <h3 className="text-2xl sm:text-3xl font-black uppercase leading-tight mb-1">
            Scan to open this tour
          </h3>
          <p className="text-slate-300 text-sm sm:text-base mb-5">
            Hand your friend their phone, point the camera at the QR — they land on{' '}
            <span className="font-semibold text-white">{tour.title}</span> instantly. No app, no
            account, no friction.
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleShare}
              data-testid="share-tour-qr-share-btn"
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 font-bold rounded-full px-4 py-2.5 text-sm shadow active:scale-95 transition-all"
            >
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copied' : 'Share link'}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              data-testid="share-tour-qr-download-btn"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 hover:bg-white/15 text-white font-bold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-all"
            >
              <Download className="w-4 h-4" />
              Save QR
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(tourUrl);
                  setCopied(true);
                  toast.success('Link copied');
                  setTimeout(() => setCopied(false), 2500);
                } catch (_) {
                  toast.error('Could not copy the link');
                }
              }}
              data-testid="share-tour-qr-copy-btn"
              className="inline-flex items-center gap-2 bg-transparent border border-white/15 hover:bg-white/10 text-white/85 hover:text-white font-bold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-all"
            >
              <Copy className="w-4 h-4" />
              Copy link
            </button>
          </div>
        </div>

        {/* Right — QR pass */}
        <div className="justify-self-center sm:justify-self-end self-center">
          <div
            ref={qrWrapperRef}
            data-testid="share-tour-qr-canvas"
            className="bg-white rounded-2xl p-4 shadow-lg ring-1 ring-white/30"
          >
            <QRCodeCanvas
              value={tourUrl}
              size={168}
              level="H"
              includeMargin={false}
              fgColor="#0f172a"
              bgColor="#ffffff"
            />
            <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mt-2">
              {tour.city || 'Audiostride'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareTourQRCard;
